// レシート一覧の絞り込み・並び替えを URL クエリと API クエリの間で受け渡す。
//
// 条件は URL に置く（戻ってきたときに同じ一覧が出る / 共有できる）。
import { endOfMonth, endOfYear, format, startOfMonth, startOfYear, subMonths } from 'date-fns'

import type {
  GetReceiptsQuery,
  ReceiptFilterFlag,
  ReceiptReviewStatus,
  ReceiptSortKey,
  ReceiptSortOrder,
} from '@/api/types/receipt'

import { isReceiptFilterFlag } from './receipt-filters'

export type ReceiptListFilters = {
  /** 購入日の範囲（JST の YYYY-MM-DD） */
  dateFrom: string | null
  dateTo: string | null
  amountMin: number | null
  amountMax: number | null
  prefectures: string[]
  flags: ReceiptFilterFlag[]
  sort: ReceiptSortKey
  order: ReceiptSortOrder
}

export const DEFAULT_FILTERS: ReceiptListFilters = {
  dateFrom: null,
  dateTo: null,
  amountMin: null,
  amountMax: null,
  prefectures: [],
  flags: [],
  sort: 'purchased_at',
  order: 'desc',
}

/** 絞り込みに使う URL クエリのキー。タブや選択中の id とは別に、まとめて消せるようにしておく */
export const FILTER_PARAM_KEYS = ['from', 'to', 'min', 'max', 'pref', 'flags', 'sort', 'order'] as const

export const SORT_OPTIONS: readonly { value: ReceiptSortKey; label: string }[] = [
  { value: 'purchased_at', label: '購入日' },
  { value: 'total_amount', label: '金額' },
  { value: 'store_name', label: '店名' },
]

export type ReceiptPeriodPreset = 'this_month' | 'last_month' | 'this_year'

export const PERIOD_PRESETS: readonly { value: ReceiptPeriodPreset; label: string }[] = [
  { value: 'this_month', label: '今月' },
  { value: 'last_month', label: '先月' },
  { value: 'this_year', label: '今年' },
]

const toDay = (date: Date): string => format(date, 'yyyy-MM-dd')

export const periodRange = (preset: ReceiptPeriodPreset, now: Date = new Date()): { from: string; to: string } => {
  if (preset === 'this_month') return { from: toDay(startOfMonth(now)), to: toDay(endOfMonth(now)) }
  if (preset === 'last_month') {
    const previous = subMonths(now, 1)
    return { from: toDay(startOfMonth(previous)), to: toDay(endOfMonth(previous)) }
  }
  return { from: toDay(startOfYear(now)), to: toDay(endOfYear(now)) }
}

/** いま選ばれている期間がどのプリセットと同じか（どれとも違えば null） */
export const matchedPreset = (
  filters: Pick<ReceiptListFilters, 'dateFrom' | 'dateTo'>,
  now: Date = new Date(),
): ReceiptPeriodPreset | null => {
  const found = PERIOD_PRESETS.find(({ value }) => {
    const range = periodRange(value, now)
    return range.from === filters.dateFrom && range.to === filters.dateTo
  })
  return found?.value ?? null
}

const readAmount = (raw: string | null): number | null => {
  if (!raw) return null
  const value = Number.parseInt(raw, 10)
  return Number.isFinite(value) && value >= 0 ? value : null
}

const isSortKey = (value: string): value is ReceiptSortKey => SORT_OPTIONS.some((option) => option.value === value)

export const readFilters = (params: URLSearchParams): ReceiptListFilters => {
  const sort = params.get('sort') ?? ''
  return {
    dateFrom: params.get('from'),
    dateTo: params.get('to'),
    amountMin: readAmount(params.get('min')),
    amountMax: readAmount(params.get('max')),
    prefectures: (params.get('pref') ?? '').split(',').filter(Boolean),
    flags: (params.get('flags') ?? '').split(',').filter(isReceiptFilterFlag),
    sort: isSortKey(sort) ? sort : DEFAULT_FILTERS.sort,
    order: params.get('order') === 'asc' ? 'asc' : DEFAULT_FILTERS.order,
  }
}

/** URL クエリへ書き戻す。既定値のものはキーごと落として URL を短く保つ */
export const writeFilters = (params: URLSearchParams, filters: ReceiptListFilters): URLSearchParams => {
  const next = new URLSearchParams(params)
  const values: Record<(typeof FILTER_PARAM_KEYS)[number], string | null> = {
    from: filters.dateFrom,
    to: filters.dateTo,
    min: filters.amountMin === null ? null : String(filters.amountMin),
    max: filters.amountMax === null ? null : String(filters.amountMax),
    pref: filters.prefectures.length > 0 ? filters.prefectures.join(',') : null,
    flags: filters.flags.length > 0 ? filters.flags.join(',') : null,
    sort: filters.sort === DEFAULT_FILTERS.sort ? null : filters.sort,
    order: filters.order === DEFAULT_FILTERS.order ? null : filters.order,
  }
  for (const key of FILTER_PARAM_KEYS) {
    const value = values[key]
    if (value === null) next.delete(key)
    else next.set(key, value)
  }
  return next
}

export const toReceiptsQuery = (
  filters: ReceiptListFilters,
  base: { review_status?: ReceiptReviewStatus; store_name?: string },
): GetReceiptsQuery => ({
  ...base,
  date_from: filters.dateFrom ?? undefined,
  date_to: filters.dateTo ?? undefined,
  amount_min: filters.amountMin ?? undefined,
  amount_max: filters.amountMax ?? undefined,
  prefecture: filters.prefectures.length > 0 ? filters.prefectures : undefined,
  flag: filters.flags.length > 0 ? filters.flags : undefined,
  sort: filters.sort,
  order: filters.order,
})

/** 他画面から詳細を開くときの location.state。Header の戻るで一覧ではなく元の画面へ戻す */
export const RECEIPT_RETURN_STATE = { returnOnClose: true } as const

export const shouldReturnOnClose = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && 'returnOnClose' in state && state.returnOnClose === true

/** 絞り込みボタンのバッジに出す、いま効いている条件の数（チップは別に出ているので数えない） */
export const activeFilterCount = (filters: ReceiptListFilters): number => {
  let count = 0
  if (filters.dateFrom || filters.dateTo) count += 1
  if (filters.amountMin !== null || filters.amountMax !== null) count += 1
  count += filters.prefectures.length
  return count
}
